import { useState, useSyncExternalStore } from 'react'
import { Link } from 'react-router-dom'
import { onlineManager } from '@tanstack/react-query'
import type {
  ClassComparisonRowOutput,
  ComparisonMetricOutput,
  ShowEditionOutput,
  ShowStatsComparisonOutput,
  TShow,
} from '@schemas'
import { useShowStatsComparison } from '@/api/hooks'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  resolveCategoryCode,
  getCategoryBadgeClass,
  getCategoryLabel,
  getCategoryRowClass,
} from '@/lib/classColors'
import { cn } from '@/lib/utils'
import { STATUS_LABELS } from '@/lib/show-status'

export function formatComparisonValue(value: number | null, average = false, signed = false) {
  if (value === null) return '—'
  const formatted = average ? Math.abs(value).toFixed(2) : Math.abs(value).toLocaleString()
  return `${value < 0 ? '−' : signed ? '+' : ''}${formatted}`
}

function MetricCells({
  metric,
  average = false,
  compare = true,
}: {
  metric: ComparisonMetricOutput
  average?: boolean
  compare?: boolean
}) {
  const percent =
    metric.percentChange === null
      ? metric.previous === 0 && metric.current !== null && metric.current > 0
        ? 'New'
        : '—'
      : `${metric.percentChange < 0 ? '−' : '+'}${Math.abs(metric.percentChange).toFixed(1)}%`
  return (
    <>
      <td className="p-3 text-right tabular-nums">
        {formatComparisonValue(metric.current, average)}
      </td>
      {compare && (
        <>
          <td className="p-3 text-right tabular-nums">
            {formatComparisonValue(metric.previous, average)}
          </td>
          <td className="p-3 text-right tabular-nums">
            {formatComparisonValue(metric.absoluteChange, average, true)}
          </td>
          <td className="p-3 text-right tabular-nums">{percent}</td>
        </>
      )}
    </>
  )
}

function TableHead({ label, compare = true }: { label: string; compare?: boolean }) {
  return (
    <thead>
      <tr className="border-b">
        {[
          label,
          compare ? 'Current' : 'Entries',
          ...(compare ? ['Previous', 'Change', '% change'] : []),
        ].map((title, index) => (
          <th key={title} scope="col" className={`p-3 ${index ? 'text-right' : 'text-left'}`}>
            {title}
          </th>
        ))}
      </tr>
    </thead>
  )
}

function EditionLabel({ show }: { show: ShowEditionOutput }) {
  return (
    <p>
      {show.name} ({show.editionYear}) · {STATUS_LABELS[show.status]}
      {show.status !== 'finalized' && <strong> · Provisional</strong>}
    </p>
  )
}

const linkClass = 'font-medium underline underline-offset-4'

/** Why a class has no counterpart on its row. Rows from the previous show are labelled as such. */
const ROW_NOTES: Record<NonNullable<ClassComparisonRowOutput['matchStatus']>, string | null> = {
  aligned: null,
  current_only: 'Not offered in the previous show',
  previous_only: null,
  unmatched: 'Left without a match',
  unresolved: 'Not matched across years yet',
}

/** The series decides the previous show, so each reason points at where the series is managed. */
function NoComparison({
  state,
  show,
}: {
  state: ShowStatsComparisonOutput['comparisonState']
  show: TShow
}) {
  const membership = show.seriesMembership
  const series = membership?.series
  if (state === 'available') return null
  return (
    <p className="text-sm text-muted-foreground">
      {state === 'no_series' ? (
        <>
          This show is not in a series, so there is no earlier show to compare it with.{' '}
          {membership?.canEdit ? (
            <>
              Add it to a series in{' '}
              <Link className={linkClass} to={`/${show.slug}/setup`}>
                Show Setup
              </Link>
              .
            </>
          ) : (
            'An organizer can add it to a series in Show Setup.'
          )}
        </>
      ) : state === 'first_edition' ? (
        <>
          This is the earliest show in {series?.name ?? 'its series'}, so there is no earlier show
          to compare it with.
          {series && (
            <>
              {' '}
              If an earlier show is missing, add it in{' '}
              <Link className={linkClass} to={`/show-series/${series.id}/settings`}>
                Series settings
              </Link>
              .
            </>
          )}
        </>
      ) : (
        <>
          The previous show in {series?.name ?? 'this series'} is not available to you, so the
          comparison is hidden. You need report access to that show.
        </>
      )}
    </p>
  )
}

export function ShowEditionComparison({ show }: { show: TShow }) {
  const [categoryFilter, setCategoryFilter] = useState<{
    showId: string | undefined
    code: string | null
  }>({ showId: show.id, code: null })
  const category = categoryFilter.showId === show.id ? categoryFilter.code : null
  const query = useShowStatsComparison(show)
  const online = useSyncExternalStore(
    callback => onlineManager.subscribe(callback),
    () => onlineManager.isOnline(),
    () => true
  )
  const data = query.data?.currentShow.id === show.id ? query.data : undefined
  const compare = data?.comparisonState === 'available' && !!data.previousShow
  const series = show.seriesMembership?.series
  const unresolved =
    (data?.alignment.current?.unresolved.classes ?? 0) +
    (data?.alignment.previous?.unresolved.classes ?? 0)
  const filteredClasses =
    data?.classes.filter(
      row =>
        !category ||
        resolveCategoryCode(
          (row.currentClass ?? row.previousClass)?.categoryCode,
          (row.currentClass ?? row.previousClass)?.division.code ?? ''
        ) === category
    ) ?? []
  return (
    <Card>
      <CardHeader className="gap-3 border-b">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>{compare ? 'Compared with the previous show' : 'Show entries'}</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching || !online}
          >
            Refresh counts
          </Button>
        </div>
        {data && !query.isError && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
            <span>
              Updated{' '}
              <time dateTime={data.generatedAt} title={new Date(data.generatedAt).toLocaleString()}>
                {new Date(data.generatedAt).toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </time>
            </span>
            <Badge variant="secondary">
              {data.currentShow.status === 'finalized' ? 'Finalized' : 'In progress'}
            </Badge>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {!online && (
          <p role="status" className="text-sm text-muted-foreground">
            Offline — cached figures are stale. Unsynced intake is not included.
          </p>
        )}
        {query.isError ? (
          <div role="alert" className="space-y-2">
            <p>Unable to load the show comparison.</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => void query.refetch()}
              disabled={!online}
            >
              Retry comparison
            </Button>
          </div>
        ) : !data ? (
          <p role="status">
            {online ? 'Loading show comparison…' : 'Connect to load the show comparison.'}
          </p>
        ) : (
          <>
            {compare && data.previousShow && (
              <div className="space-y-1 text-sm">
                <p className="font-semibold">
                  Compared with {data.previousShow.name} ({data.previousShow.editionYear})
                </p>
                <EditionLabel show={data.previousShow} />
              </div>
            )}
            <NoComparison state={data.comparisonState} show={show} />
            {compare && (
              <div
                role="region"
                aria-label="Show comparison summary"
                tabIndex={0}
                className="overflow-x-auto"
              >
                <table className={cn('w-full text-sm', compare && 'min-w-[36rem]')}>
                  <caption className="sr-only">Entry and entrant totals for both shows</caption>
                  <TableHead label="Metric" />
                  <tbody>
                    <tr className="border-b">
                      <th scope="row" className="p-3 text-left">
                        Entries
                      </th>
                      <MetricCells metric={data.summary.entriesCount} />
                    </tr>
                    <tr className="border-b">
                      <th scope="row" className="p-3 text-left">
                        Entrants
                      </th>
                      <MetricCells metric={data.summary.entrantsCount} />
                    </tr>
                    <tr>
                      <th scope="row" className="p-3 text-left">
                        Average entries per entrant
                      </th>
                      <MetricCells metric={data.summary.averageEntriesPerEntrant} average />
                    </tr>
                  </tbody>
                </table>
                {data.summary.averageEntriesPerEntrant.current === null && <p>No entrants yet.</p>}
                {data.previousShow && data.summary.averageEntriesPerEntrant.previous === null && (
                  <p>No entrants in the previous show.</p>
                )}
              </div>
            )}
            <section className="space-y-2" aria-label="Entries by class">
              {compare && (
                <p className="text-sm text-muted-foreground">
                  “—” means the other show has no matching class.{' '}
                  {unresolved > 0 &&
                    `${unresolved} ${unresolved === 1 ? 'class has' : 'classes have'} not been matched across years yet. `}
                  {series ? (
                    <>
                      Classes are matched for the whole series in{' '}
                      <Link className={linkClass} to={`/show-series/${series.id}/alignment`}>
                        Match classes across years
                      </Link>
                      .
                    </>
                  ) : (
                    'Classes are matched for the whole series.'
                  )}
                </p>
              )}
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Category</legend>
                <div className="flex flex-wrap gap-2">
                  {[null, 'H', 'W', 'A', 'B', 'F', 'N'].map(code => (
                    <button
                      key={code ?? 'all'}
                      type="button"
                      aria-pressed={category === code}
                      onClick={() => setCategoryFilter({ showId: show.id, code })}
                      className={cn(
                        'min-h-8 rounded-md border px-3 py-1 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground',
                        code ? getCategoryBadgeClass(code) : 'bg-background text-foreground',
                        category === code
                          ? 'ring-2 ring-foreground ring-offset-2 ring-offset-background'
                          : undefined
                      )}
                    >
                      {code ? getCategoryLabel(code) : 'All'}
                    </button>
                  ))}
                </div>
              </fieldset>
              <p className="text-sm" aria-live="polite" aria-atomic="true">
                Showing {filteredClasses.length} of {data.classes.length} classes
              </p>
              <div
                role="region"
                aria-label={compare ? 'Class comparison table' : 'Class entries table'}
                tabIndex={0}
                className="overflow-x-auto"
              >
                <table className={cn('w-full text-sm', compare && 'min-w-[36rem]')}>
                  <caption className="sr-only">
                    {compare ? 'Entries by class across both shows' : 'Entries by class'}
                  </caption>
                  <TableHead label="Class" compare={compare} />
                  <tbody>
                    {filteredClasses.map(row => {
                      const current = row.currentClass,
                        previous = row.previousClass
                      const title = current ?? previous!
                      return (
                        <tr
                          key={row.key}
                          className={cn(
                            'border-b text-foreground',
                            getCategoryRowClass(
                              resolveCategoryCode(title.categoryCode, title.division.code)
                            )
                          )}
                        >
                          <th scope="row" className="p-3 text-left font-normal">
                            <span>
                              {title.division.code} · {title.code} {title.name}
                            </span>
                            {previous && current && (
                              <span className="block text-xs">
                                Previous: {previous.division.code} · {previous.code} {previous.name}
                              </span>
                            )}
                            {row.matchStatus && row.matchStatus !== 'aligned' && (
                              <span className="block text-xs">
                                {[!current && 'Previous show only', ROW_NOTES[row.matchStatus]]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            )}
                          </th>
                          <MetricCells metric={row} compare={compare} />
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {!data.classes.length ? (
                <p>No classes configured.</p>
              ) : !filteredClasses.length ? (
                <p>No classes in this category. Choose All to see every class.</p>
              ) : null}
            </section>
            {show.enableClubs !== false && (
              <section className="space-y-2" aria-label="Entries by club">
                <h3 className="font-semibold">Entries by club</h3>
                <p className="text-sm text-muted-foreground">
                  Each entry uses its stored club affiliation. The Club Participation report
                  excludes drafts and can include registered entrants without entries; its totals
                  may differ.
                </p>
                {!data.clubs.currentAvailable && (
                  <p>Current show: Club tracking was disabled for this show.</p>
                )}
                {data.previousShow && !data.clubs.previousAvailable && (
                  <p>Previous show: Club tracking was disabled for this show.</p>
                )}
                <div
                  role="region"
                  aria-label={compare ? 'Club comparison table' : 'Club entries table'}
                  tabIndex={0}
                  className="overflow-x-auto"
                >
                  <table className={cn('w-full text-sm', compare && 'min-w-[36rem]')}>
                    <caption className="sr-only">Entries attributed to each club</caption>
                    <TableHead label="Club" compare={compare} />
                    <tbody>
                      {data.clubs.rows.map(row => (
                        <tr key={row.key} className="border-b">
                          <th scope="row" className="p-3 text-left font-normal">
                            {row.currentLabel ?? row.previousLabel}
                            {row.currentLabel &&
                              row.previousLabel &&
                              row.currentLabel !== row.previousLabel && (
                                <span className="block text-xs">Previous: {row.previousLabel}</span>
                              )}
                          </th>
                          <MetricCells metric={row} compare={compare} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!data.clubs.rows.length && <p>No club entries to display.</p>}
              </section>
            )}
            {compare && (
              <p className="text-xs text-muted-foreground">
                Changes show how much more or less this show has than the previous show. “New” means
                the previous show had none.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
